import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import { useTranslation } from "react-i18next";
import { Gauge, ChevronRight } from "lucide-react";

const AXIS_KEYS = {
  tolerances_score: "mfgScore.axTolerances",
  setups_score: "mfgScore.axSetups",
  geometry_score: "mfgScore.axGeometry",
  material_score: "mfgScore.axMaterial",
  surface_score: "mfgScore.axSurface",
  risk_score: "mfgScore.axRisk",
};

function scoreColor(score) {
  if (score >= 75) return { hex: "#34d399", txt: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/40", labelKey: "mfgScore.excellent" };
  if (score >= 55) return { hex: "#60a5fa", txt: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/40", labelKey: "mfgScore.good" };
  if (score >= 35) return { hex: "#f59e0b", txt: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/40", labelKey: "mfgScore.critical" };
  return { hex: "#ef4444", txt: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/40", labelKey: "mfgScore.difficult" };
}

export default function ManufacturabilityScore({ score }) {
  const { t } = useTranslation();
  if (!score) return null;
  const c = scoreColor(score.overall);
  const data = Object.keys(AXIS_KEYS).map((k) => ({
    axis: t(AXIS_KEYS[k]),
    value: Math.max(0, Math.min(100, Math.round(score[k] || 0))),
  }));

  return (
    <div className="tech-card overflow-hidden" data-testid="manufacturability-score">
      <div className="px-5 py-3 border-b border-slate-700/60 flex items-center justify-between">
        <div className="label-tech flex items-center gap-2">
          <Gauge size={12} className="text-blue-400"/> CNC Manufacturability Score™
        </div>
        <span className={`text-[10px] label-tech px-2 py-1 rounded border ${c.border} ${c.bg} ${c.txt}`}>{t(c.labelKey)}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-5 p-5">
        <div className="flex flex-col items-center justify-center">
          <div className={`relative w-32 h-32 flex items-center justify-center rounded-full ${c.bg} border-2 ${c.border}`}>
            <div className={`font-mono-data text-4xl font-bold ${c.txt}`}>{Math.round(score.overall)}</div>
            <div className="absolute bottom-1 text-[10px] text-slate-500 label-tech">/ 100</div>
          </div>
          <div className="mt-3 text-xs text-slate-400 text-center max-w-[180px] leading-snug">{score.rationale}</div>
        </div>

        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={data} outerRadius="80%">
              <PolarGrid stroke="#334155"/>
              <PolarAngleAxis dataKey="axis" tick={{ fill: "#94a3b8", fontSize: 11 }}/>
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "#475569", fontSize: 9 }} tickCount={5}/>
              <Radar dataKey="value" stroke={c.hex} fill={c.hex} fillOpacity={0.25} strokeWidth={2}/>
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="px-5 pb-5 grid grid-cols-2 md:grid-cols-3 gap-2">
        {Object.entries(AXIS_KEYS).map(([k, labelKey]) => {
          const v = Math.round(score[k] || 0);
          const ac = scoreColor(v);
          return (
            <div key={k} className="flex items-center justify-between px-3 py-2 rounded bg-slate-950 border border-slate-800" data-testid={`score-axis-${k}`}>
              <span className="text-xs text-slate-400 flex items-center gap-1"><ChevronRight size={10}/>{t(labelKey)}</span>
              <span className={`font-mono-data text-sm ${ac.txt}`}>{v}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
